// §4.11 Adjustment Approvals (office) — added in the phase-3a fix round, and
// recorded as a new §4 row in the same PR.
//
// WHY IT EXISTS. §3.9's value gate holds an over-threshold (or unvalued —
// edge case 14) adjustment instead of posting it: POST /movements answers 202
// with an `adjustment_request_id` and NO movement is written. The office slice
// rendered that as one line of text on the Move panel and stopped. The request
// then existed only in the database: the requester could not cancel it, the
// approver had no inbox, and §5's `/adjustment-requests` trio (list, decide,
// cancel) had no caller at all. A hold nobody can see is worse than a refusal.
//
// The D-049 LOCAL tier decides today (Module 12's chain is unbound): decide is
// `inventory.count.review`, cancel is `inventory.adjust`, and the service
// narrows which tier may decide which value band. The client mirrors those two
// codes for the CONTROLS; the server is the authority, and its refusal text is
// rendered verbatim through lib/errors.ts.
//
// MONEY. `est_value_minor` is redactable exactly like every other module-06
// money field, so the column follows the §4-preamble presence rule: no visible
// value on the page ⇒ no column ⇒ a note saying whether it was withheld from
// this role or the stock is genuinely unvalued. An approver who cannot see the
// value is told so, because "approve this" and "approve this blind" are
// different asks.

import { enumLabel } from "../../../../lib/enum-label";
import { shortId } from "../../../../lib/short-id";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { EmptyState, ErrorState, TableSkeleton } from "../../../../components/states";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { hasLiteralRole } from "../../../../lib/role-codes";
import { AdjustStockLink } from "../components/adjust-stock-link";
import { FacilityChip } from "../components/filters";
import { HeldAdjustment, usePayloadRefs } from "../components/held-adjustment";
import { InventoryStatus, MoneyCell, MoneyColumnNote, RefLabel } from "../components/inventory-ui";
import {
  useAdjustmentRequests,
  useCancelAdjustmentRequest,
  useDecideAdjustmentRequest,
  usePrincipalIndex,
  type AdjustmentRequest,
  type AdjustmentRequestsQuery,
} from "../hooks/inventory-queries";
import { useInventoryPermissions } from "../hooks/permissions";
import { costPresence, showCostColumn } from "../lib/cost";
import { inventoryErrorMessage } from "../lib/errors";
import { formatTimestamp } from "../lib/measure";

// §2.12's own vocabulary — a request is `pending_approval` until it is
// `posted` (approved and written), `rejected`, or `cancelled` by its requester.
const STATUSES = ["pending_approval", "posted", "rejected", "cancelled"] as const;

/**
 * Requests per page (spec 06 §4.11, 2026-10-18). The read is cursor-paged, so
 * the screen asks for one page at a time and holds a cursor stack. At fifty a
 * page the list measured 4,006 px at 1440x900 (W-SWEEP, staging 7eb5c956);
 * twenty rows keep a full page near two screens in en and ru.
 */
export const APPROVALS_PAGE_SIZE = 20;

export function AdjustmentApprovalsScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const perms = useInventoryPermissions();

  const [status, setStatus] = useState("pending_approval");
  const [facilityId, setFacilityId] = useState("");
  const [acting, setActing] = useState<{
    id: string;
    action: "approve" | "reject" | "cancel";
  } | null>(null);
  const [note, setNote] = useState("");

  // A cursor stack keyed to the filters: a status or facility change starts
  // again from page one, adjusted during render so a stale page never paints.
  const pageKey = `${status}|${facilityId}`;
  const [paging, setPaging] = useState<{ key: string; cursors: (string | undefined)[] }>({
    key: pageKey,
    cursors: [undefined],
  });
  if (paging.key !== pageKey) setPaging({ key: pageKey, cursors: [undefined] });
  const cursors = paging.key === pageKey ? paging.cursors : [undefined];
  const pageIndex = cursors.length - 1;
  const cursor = cursors[pageIndex];

  const query = useMemo(() => {
    const next: AdjustmentRequestsQuery = { limit: APPROVALS_PAGE_SIZE };
    if (status.length > 0) next.status = status;
    if (facilityId.length > 0) next.facility_id = facilityId;
    if (cursor !== undefined) next.cursor = cursor;
    return next;
  }, [status, facilityId, cursor]);

  const requests = useAdjustmentRequests(query);
  const decide = useDecideAdjustmentRequest();
  const cancel = useCancelAdjustmentRequest();
  const principals = usePrincipalIndex(perms.canReadMembers);

  useEffect(() => {
    if (requests.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [requests.error, navigate]);

  const items = requests.data?.items ?? [];
  const nextCursor = requests.data?.next_cursor ?? null;
  // Deciding the last requests of a later page can leave it empty: step back
  // a page rather than say "nothing waiting" while page one still has some.
  if (requests.data !== undefined && items.length === 0 && pageIndex > 0) {
    setPaging({ key: pageKey, cursors: cursors.slice(0, -1) });
  }
  // §4.12 (D-315): the held movement in WORDS — product, batch, place,
  // direction and what it is booked against — resolved once for the page.
  const refs = usePayloadRefs(items.map((request) => request.movement_payload));
  const presence = costPresence(items, (row) => row.est_value_minor);
  const withValue = showCostColumn(presence);
  // §3.9 gives adjustments no auto tier: only facility_admin / group_admin
  // decide one, so finance — which holds count.review for COUNTS — is offered
  // no Approve it would be refused (D-315, same rule as the request page).
  const canDecide =
    perms.has("inventory.count.review") &&
    // LITERAL-ROLE-CODES: `adjustments.service.ts` decides on the literal admin codes (approverCapability).
    hasLiteralRole(perms, ["group_admin", "facility_admin"]);
  const canCancel = perms.has("inventory.adjust");

  function submit(request: AdjustmentRequest, action: "approve" | "reject" | "cancel") {
    const trimmed = note.trim();
    if (action === "cancel") {
      cancel.mutate({ id: request.id, ...(trimmed.length > 0 ? { note: trimmed } : {}) });
    } else {
      decide.mutate({ id: request.id, action, ...(trimmed.length > 0 ? { note: trimmed } : {}) });
    }
    setActing(null);
    setNote("");
  }

  const columns: DataTableColumn<AdjustmentRequest>[] = [
    {
      key: "requested_at",
      header: t("inventory.approvals.colRequested"),
      id: true,
      render: (request) => formatTimestamp(request.requested_at, i18n.language),
    },
    {
      key: "requested_by",
      header: t("inventory.approvals.colBy"),
      render: (request) => (
        <RefLabel
          value={principals.byId.get(request.requested_by)}
          id={request.requested_by}
          pending={principals.isPending}
          kind="user"
        />
      ),
    },
    {
      key: "movement_payload",
      header: t("inventory.approvals.colWhat"),
      render: (request) => (
        <>
          {/* The requester's note is one clipped line (rev-7 audit #10): in
              full it turned every row into a five-line wall, and left out it
              let an approver press Approve without being shown why it was
              asked. The whole note is on hover and on the request's page. */}
          <HeldAdjustment
            payload={request.movement_payload}
            refs={refs}
            withNote="line"
            testId={`approval-words-${request.id}`}
          />{" "}
          <Link
            to="/inventory/approvals/$id"
            params={{ id: request.id }}
            className="ds-lnk"
            data-testid={`approval-open-${request.id}`}
          >
            {t("inventory.approvals.openRequest")}
          </Link>
          {request.posted_movement_id !== null ? (
            <>
              {" "}
              <Link
                to="/inventory/movements"
                search={{ source_doc_type: "manual_adjustment", source_doc_id: request.id }}
                className="ds-lnk"
              >
                {t("inventory.approvals.postedMovement")}
              </Link>
            </>
          ) : null}
          {request.reject_reason !== null ? (
            <span className="inv-hint">
              {" "}
              {/* `rejectReason.*`, not `reject.*`: `reject` is the button
                  label, and one i18n path cannot be both a string and a
                  namespace. A human rejection is free text and renders as
                  itself through defaultValue; only §3.9's `stale_stock`
                  sentinel has copy. */}
              {enumLabel(t, "inventory.approvals.rejectReason", request.reject_reason)}
            </span>
          ) : null}
        </>
      ),
    },
    // §7 redaction-by-absence: no value column at all for a caller the server
    // did not serve `est_value_minor` to.
    ...(withValue
      ? [
          {
            key: "est_value_minor",
            header: t("inventory.approvals.colValue"),
            numeric: true,
            render: (request: AdjustmentRequest) => (
              <MoneyCell
                row={request}
                valueOf={(row) => row.est_value_minor}
                currencyOf={(row) => row.currency}
              />
            ),
          },
        ]
      : []),
    {
      key: "status",
      header: t("inventory.approvals.colStatus"),
      render: (request) => <InventoryStatus status={request.status} scope="adjustmentRequest" />,
    },
    {
      key: "actions",
      header: t("inventory.countReview.colActions"),
      actions: true,
      render: (request) =>
        request.status !== "pending_approval" ? (
          "—"
        ) : acting?.id === request.id ? (
          <form
            className="inline-form"
            onSubmit={(event) => {
              event.preventDefault();
              submit(request, acting.action);
            }}
          >
            <p className="inv-warning">
              {acting.action === "approve"
                ? t("inventory.approvals.approveWarning")
                : acting.action === "reject"
                  ? t("inventory.approvals.rejectWarning")
                  : t("inventory.approvals.cancelWarning")}
            </p>
            <Field
              label={
                acting.action === "reject"
                  ? t("inventory.approvals.noteRequired")
                  : t("inventory.approvals.note")
              }
            >
              <input
                className="ds-input"
                value={note}
                onChange={(event) => {
                  setNote(event.target.value);
                }}
                // The server refuses a reject without a note (400
                // VALIDATION_FAILED) — asking here is cheaper than a round trip.
                required={acting.action === "reject"}
              />
            </Field>
            <Button
              type="submit"
              variant="primary"
              busy={decide.isPending || cancel.isPending}
              disabled={acting.action === "reject" && note.trim().length === 0}
            >
              {t("inventory.common.confirm")}
            </Button>
            <Button
              onClick={() => {
                setActing(null);
                setNote("");
              }}
            >
              {t("inventory.common.cancel")}
            </Button>
          </form>
        ) : (
          <div className="row-actions">
            {canDecide ? (
              <>
                <Button
                  onClick={() => {
                    setActing({ id: request.id, action: "approve" });
                    setNote("");
                  }}
                >
                  {t("inventory.approvals.approve")}
                </Button>
                <Button
                  onClick={() => {
                    setActing({ id: request.id, action: "reject" });
                    setNote("");
                  }}
                >
                  {t("inventory.approvals.reject")}
                </Button>
              </>
            ) : null}
            {canCancel ? (
              <Button
                onClick={() => {
                  setActing({ id: request.id, action: "cancel" });
                  setNote("");
                }}
              >
                {t("inventory.approvals.cancel")}
              </Button>
            ) : null}
            {!canDecide && !canCancel ? (
              <span className="inv-hint">{t("inventory.approvals.readOnly")}</span>
            ) : null}
          </div>
        ),
    },
  ];

  return (
    <section className="adjustment-approvals">
      <PageHeader
        title={t("inventory.approvals.title")}
        actions={
          <>
            <AdjustStockLink
              search={{ from: "approvals" }}
              label={t("inventory.approvals.newAdjustment")}
              testId="approvals-new-adjustment"
            />
            <FacilityChip value={facilityId} onChange={setFacilityId} />
          </>
        }
      />

      <p className="inv-hint">{t("inventory.approvals.localTierNote")}</p>

      <div className="filter-bar">
        <label className="filter-chip">
          <span className="filter-chip-label">{t("inventory.approvals.status")}</span>
          <select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
            }}
          >
            <option value="">{t("inventory.approvals.allStatuses")}</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {enumLabel(t, "inventory.vocab.adjustmentRequest", value)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {requests.isPending ? (
        <TableSkeleton rows={5} cols={5} label={t("inventory.approvals.loading")} />
      ) : null}

      {requests.error && !(requests.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("inventory.approvals.errorTitle")}
          detail={requests.error.message}
          retryLabel={t("inventory.common.retry")}
          onRetry={() => void requests.refetch()}
          requestId={requestIdOf(requests.error)}
          requestIdLabel={t("inventory.common.requestId")}
        />
      ) : null}

      {requests.data !== undefined && items.length === 0 ? (
        <EmptyState
          title={
            status === "pending_approval"
              ? t("inventory.approvals.emptyPendingTitle")
              : t("inventory.approvals.emptyTitle")
          }
          body={
            status === "pending_approval"
              ? t("inventory.approvals.emptyPendingBody")
              : t("inventory.approvals.emptyBody")
          }
        />
      ) : null}

      {items.length > 0 ? (
        <>
          <MoneyColumnNote presence={presence} />
          <DataTable
            columns={columns}
            rows={items}
            rowKey={(request) => request.id}
            empty={null}
            pager={
              pageIndex > 0 || nextCursor !== null
                ? {
                    range: t("inventory.approvals.pageRange", {
                      from: pageIndex * APPROVALS_PAGE_SIZE + 1,
                      to: pageIndex * APPROVALS_PAGE_SIZE + items.length,
                    }),
                    page: pageIndex + 1,
                    onPage: (n) => {
                      if (n > pageIndex + 1 && nextCursor !== null) {
                        setPaging({ key: pageKey, cursors: [...cursors, nextCursor] });
                      } else if (n < pageIndex + 1) {
                        setPaging({ key: pageKey, cursors: cursors.slice(0, -1) });
                      }
                      setActing(null);
                      setNote("");
                    },
                    prevLabel: t("inventory.filters.prev"),
                    nextLabel: t("inventory.filters.next"),
                    hasPrev: pageIndex > 0,
                    hasNext: nextCursor !== null,
                  }
                : undefined
            }
            data-testid="approvals-table"
          />
          {decide.isError || cancel.isError ? (
            <p className="form-error" role="alert">
              {inventoryErrorMessage(t, decide.error ?? cancel.error, "inventory.approvals.failed")}
            </p>
          ) : null}
          {decide.isSuccess ? (
            <p className="inv-success" role="status">
              {decide.data.request.status === "posted"
                ? t("inventory.approvals.approved", {
                    id:
                      decide.data.request.posted_movement_id === null
                        ? ""
                        : shortId(decide.data.request.posted_movement_id),
                  })
                : t("inventory.approvals.rejected")}
            </p>
          ) : null}
          {cancel.isSuccess ? (
            <p className="inv-success" role="status">
              {t("inventory.approvals.cancelled")}
            </p>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
